/**
 * API Service Module
 * Centralized API communication with error handling
 */

import { CONFIG } from '../config/client.config.js';
import { sessionStore } from '../state/sessionStore';

// Decode a JWT's exp claim (no verification — client-side staleness check only)
// and report whether it's within `skewMs` of expiry. Malformed → treat as
// expiring so we re-mint.
function isJwtExpiring(token, skewMs = 60_000) {
  try {
    const [, payload] = token.split('.');
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp !== 'number' || exp * 1000 - Date.now() < skewMs;
  } catch {
    return true;
  }
}

// Decode a JWT's roomName claim (no verification — client-side scoping check
// only). Malformed or missing → null.
function jwtRoomName(token) {
  try {
    const [, payload] = token.split('.');
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).roomName || null;
  } catch {
    return null;
  }
}

class ApiService {
  constructor() {
    this.baseUrl = window.location.origin;
    this.apiBase = CONFIG.SERVER.API_BASE;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${this.apiBase}${endpoint}`;

    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.error || response.statusText,
          response.status,
          errorData.code,
          errorData.details,
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(`Network error: ${error.message}`, 0, 'NETWORK_ERROR');
    }
  }

  /**
   * Get Agora token from server
   */
  async getAgoraToken(channelName, uid = null) {
    console.log('[API] Fetching Agora token for channel:', channelName);

    // A valid session token is now REQUIRED server-side (§2.5): the meeting role
    // in the token authoritatively decides publisher vs. subscriber, and the
    // token must be scoped to this channel. Use the invite session when present,
    // otherwise mint/reuse the anonymous open-access presence token (role
    // 'guest', scoped to OPEN_ACCESS_ROOM) so open-access visitors can still
    // publish. When open access is disabled server-side, no token exists and the
    // server returns 401 — video simply stays off, the scene is unaffected.
    const sessionToken =
      localStorage.getItem('oggi_session') ||
      localStorage.getItem('oggi_presence') ||
      (await this.ensureParticipantToken()) ||
      undefined;

    const tokenData = await this.request('/agora/token', {
      method: 'POST',
      body: JSON.stringify({
        channelName,
        uid,
        sessionToken,
      }),
    });

    console.log(
      '[API] Token received, expires at:',
      new Date(tokenData.expiresAt * 1000).toISOString(),
    );
    return tokenData;
  }

  /**
   * Health check endpoint
   */
  async getHealthStatus() {
    return await this.request('/health');
  }

  /**
   * Fetch the admin-managed external videos (YouTube/Vimeo) for the theater
   * playlist. Public + non-sensitive; open-access visitors need it too. Never
   * throws — a failure yields an empty list so the theater falls back to the
   * self-hosted MP4 playlist (this keeps the e2e smoke test green without a
   * populated Supabase).
   * @returns {Promise<Array<{id:string, provider:string, videoId:string, embedHash:string|null, title:string|null}>>}
   */
  async getExternalVideos() {
    try {
      const { videos } = await this.request('/external-videos');
      return Array.isArray(videos) ? videos : [];
    } catch {
      return [];
    }
  }

  // --- Moderation (§2.6) -------------------------------------------------
  // All moderation calls carry the signed session token; the server derives
  // the room and authorizes the meeting role from it. Without a session token
  // (open-access mode) these throw and callers degrade gracefully.

  /**
   * Auth header for capability-bearing calls (moderation, room lock). These
   * require a real invite session token — the open-access presence token is
   * deliberately not accepted (it's a powerless 'guest').
   * @returns {string}
   */
  _sessionAuthHeader() {
    const sessionToken = localStorage.getItem('oggi_session');
    if (!sessionToken) {
      throw new ApiError('No session token', 401, 'MISSING_SESSION_TOKEN');
    }
    return `Bearer ${sessionToken}`;
  }

  /**
   * Auth header for participant-registry calls (join/heartbeat/leave). Prefers
   * a real invite session, falling back to the anonymous open-access presence
   * token so open-access visitors are tracked too.
   * @param {string} [explicitToken]
   * @returns {string}
   */
  _participantAuthHeader(explicitToken) {
    const token =
      explicitToken ||
      localStorage.getItem('oggi_session') ||
      localStorage.getItem('oggi_presence');
    if (!token) {
      throw new ApiError('No participant token', 401, 'MISSING_SESSION_TOKEN');
    }
    return `Bearer ${token}`;
  }

  /**
   * Ensure a participant token exists, minting an anonymous open-access
   * presence token when there's no invite session. Returns the token, or null
   * when open access is disabled server-side (so callers degrade gracefully).
   * @param {{persist?: boolean}} [options]
   * @returns {Promise<string|null>}
   */
  async ensureParticipantToken({ persist = true } = {}) {
    const session = localStorage.getItem('oggi_session');
    if (session) return session;
    const cached = localStorage.getItem('oggi_presence');
    if (cached && !isJwtExpiring(cached)) return cached;
    try {
      const { sessionToken } = await this.request('/session/open-access', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (persist) localStorage.setItem('oggi_presence', sessionToken);
      return sessionToken;
    } catch {
      return null; // open access disabled or unreachable — no presence tracking
    }
  }

  /**
   * Live participant presence for the current room — the waiting room's
   * "who's already here" line. Returns { live, names } or null whenever
   * presence is unavailable: no participant token (open access disabled), a
   * token scoped to a different room (a stale invite `oggi_session` in
   * open-access mode would otherwise report the wrong room), or any request
   * failure. Never throws — presence is cosmetic and must never block entry.
   * @returns {Promise<{live: number, names: string[]}|null>}
   */
  async getRoomPresence() {
    try {
      const token = await this.ensureParticipantToken();
      if (!token || jwtRoomName(token) !== sessionStore.getState().roomName) return null;
      return await this.request('/video-sessions/presence', {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      return null;
    }
  }

  /**
   * Register the local participant in the room's video session so moderation
   * can target them server-side. Safe to call again when the Agora uid
   * arrives — the server updates the same row.
   * @param {{photonActorNr: number, agoraUid?: number, displayName?: string, entryRequestId?: string}} params
   * @param {string} [participantToken]
   * @returns {Promise<{sessionId: string, participantId: string, locked: boolean, waitingRoomEnabled?: boolean}>}
   */
  async joinVideoSession(params, participantToken) {
    return await this.request('/video-sessions/join', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: this._participantAuthHeader(participantToken),
      },
      body: JSON.stringify(params),
    });
  }

  /**
   * Refresh the local participant's liveness so the admin dashboard can tell
   * live participants from stale rows. Called on a ~30s interval.
   * @param {{photonActorNr: number}} params
   * @returns {Promise<{ok: boolean, locked: boolean, waitingRoomEnabled?: boolean}>}
   */
  async heartbeatVideoSession(params) {
    return await this.request('/video-sessions/heartbeat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this._participantAuthHeader() },
      body: JSON.stringify(params),
    });
  }

  /**
   * Host moderation action. The server authorizes + writes the audit log;
   * for in-room targets the caller raises Photon event 53 on success so the
   * target self-enforces. The lobby decisions 'admit'/'deny' target a waiting
   * guest (not in the room yet) by entryRequestId - no Photon event; the
   * guest's entry-status poll picks the decision up.
   * @param {'mute'|'camera-off'|'stop-share'|'remove'|'admit'|'deny'} action
   * @param {{targetActorNr?: number, entryRequestId?: string, targetName?: string, actorName?: string, reason?: string}} params
   * @returns {Promise<{success: boolean, moderationEventId: string}>}
   */
  async moderationAction(action, params) {
    return await this.request(`/moderation/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this._sessionAuthHeader() },
      body: JSON.stringify(params),
    });
  }

  /**
   * Lock/unlock the caller's room (blocks new invite claims while locked).
   * @param {boolean} locked
   * @param {string} [actorName]
   * @returns {Promise<{success: boolean, locked: boolean}>}
   */
  async setRoomLock(locked, actorName) {
    return await this.request('/moderation/lock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this._sessionAuthHeader() },
      body: JSON.stringify({ locked, actorName }),
    });
  }

  // --- Live role promotion (docs/live-role-promotion.md) -------------------
  // Deliberately not folded into moderationAction: the action union and its
  // "raise Photon event 53 on success" contract don't fit, and the re-mint is
  // not a /moderation/* call at all. Both use the invite-session header, so an
  // open-access visitor (presence token only) no-ops locally with a 401.

  /**
   * Promote/demote a participant between guest and presenter. The server
   * authorizes from the JWT, writes session_participants.role plus the audit
   * row, and returns a short-lived grant the caller relays to the target over
   * Photon event 51.
   * @param {{targetActorNr: number, role: 'guest'|'presenter', targetName?: string, actorName?: string, reason?: string}} params
   * @returns {Promise<{success: boolean, role: string, grant: string, unchanged?: boolean, moderationEventId?: string}>}
   */
  async setParticipantRole(params) {
    return await this.request('/moderation/role', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this._sessionAuthHeader() },
      body: JSON.stringify(params),
    });
  }

  /**
   * Exchange a role grant for a session token carrying the new role, so the
   * promotion survives a reload. Called by the promoted actor as itself; the
   * server mints the role from its DB row, never from the grant.
   * @param {{photonActorNr: number, grant: string}} params
   * @returns {Promise<{sessionToken: string, role: string}>}
   */
  async remintSessionRole(params) {
    return await this.request('/session/role', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this._sessionAuthHeader() },
      body: JSON.stringify(params),
    });
  }

  // --- Waiting-room lobby (docs/waiting-room-approval.md) ------------------
  // The guest side (request/status/cancel) and the host side (pending list,
  // waiting-room toggle) both require a real invite session token - the
  // open-access presence token never reaches these (open access has no lobby).
  // Admit/deny ride moderationAction('admit'|'deny', { entryRequestId, ... }).

  /**
   * Ask to enter the room. Hosts/presenters, open-access tokens, and rooms
   * with the waiting room disabled come back 'admitted' immediately.
   * @param {string} [displayName]
   * @returns {Promise<{status: 'admitted'|'pending', entryRequestId?: string}>}
   */
  async requestEntry(displayName) {
    return await this.request('/video-sessions/request-entry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this._sessionAuthHeader() },
      body: JSON.stringify({ displayName }),
    });
  }

  /**
   * Poll the waiting guest's entry request. Doubles as the pending row's
   * liveness heartbeat - the host list drops guests who stop polling.
   * @param {string} entryRequestId
   * @returns {Promise<{status: string, waitingRoomEnabled: boolean}>}
   */
  async getEntryStatus(entryRequestId) {
    return await this.request(
      `/video-sessions/entry-status?id=${encodeURIComponent(entryRequestId)}`,
      {
        headers: { Authorization: this._sessionAuthHeader() },
      },
    );
  }

  /**
   * Best-effort "I stopped waiting" (Cancel button). Never throws - the 15s
   * liveness window is the backstop if this never lands.
   * @param {string} entryRequestId
   */
  async cancelEntry(entryRequestId) {
    try {
      await this.request('/video-sessions/cancel-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: this._sessionAuthHeader() },
        body: JSON.stringify({ entryRequestId }),
      });
    } catch {
      /* best-effort */
    }
  }

  /**
   * The host's 5s poll: live waiting guests + the toggle state.
   * @returns {Promise<{waitingRoomEnabled: boolean, pending: Array<{id: string, displayName: string|null, requestedAt: string}>}>}
   */
  async getPendingEntries() {
    return await this.request('/video-sessions/pending', {
      headers: { Authorization: this._sessionAuthHeader() },
    });
  }

  /**
   * Enable/disable the room's waiting room. Disabling auto-admits everyone
   * currently waiting.
   * @param {boolean} enabled
   * @param {string} [actorName]
   * @returns {Promise<{success: boolean, enabled: boolean}>}
   */
  async setWaitingRoom(enabled, actorName) {
    return await this.request('/moderation/waiting-room', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this._sessionAuthHeader() },
      body: JSON.stringify({ enabled, actorName }),
    });
  }
}

/**
 * Custom API Error class
 */
class ApiError extends Error {
  constructor(message, status, code, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export { ApiError };
